import { ExpenseEvaluationResponse } from '@module/expense-evaluation/model/ExpenseEvaluationResponse';
import { ExpenseEvaluationParametersForm } from '@module/expense-evaluation/model/ExpenseEvaluationParametersForm';
import { ExpenseEvaluationHusbandryParametersForm } from '@module/expense-evaluation/model/ExpenseEvaluationHusbandryParametersForm';
import { ExpenseEvaluationEditForm } from '@module/expense-evaluation/model/ExpenseEvaluationEditForm';
import { ExpenseEvaluationEditHusbandryForm } from '@module/expense-evaluation/model/ExpenseEvaluationEditHusbandryForm';
import { ExpenseEvaluationVerifyForm } from '@state/types/ExpenseEvaluationVerifyForm';
import { PsUtilities } from 'components';
import { evaluationApi } from '../api';

export const expenseEvaluationApi = evaluationApi.injectEndpoints({
  endpoints: (build) => ({
    verifyExpenseEvaluation: build.mutation<
      ExpenseEvaluationResponse,
      {
        evaluationId: number;
        formValues: ExpenseEvaluationVerifyForm;
        attachments?: {
          principalsAccount: File[];
          ownersAccount: File[];
          charterersAccount: File[];
        };
      }
    >({
      query: ({ evaluationId, formValues, attachments }) => {
        const formData = PsUtilities.createFormRequestParts(
          formValues,
          attachments?.principalsAccount ?? []
        );

        if (attachments) {
          PsUtilities.addAttachmentsToForm(
            formData,
            attachments.ownersAccount,
            'ownersAccountAttachments'
          );

          PsUtilities.addAttachmentsToForm(
            formData,
            attachments.charterersAccount,
            'charterersAccountAttachments'
          );
        }

        return {
          url: `/${evaluationId}/verify-expense-evaluation`,
          data: formData,
          method: 'POST',
          headers: {
            'Content-Type': 'multipart/form-data;'
          },
          formData: true
        };
      },
      invalidatesTags: (res, _err, { evaluationId }) =>
        res
          ? [
              { type: 'EVALUATION', evaluationId },
              'BudgetStatus',
              'BudgetCommit'
            ]
          : ['EVALUATION']
    }),

    saveExpenseEvaluationParameters: build.mutation<
      ExpenseEvaluationResponse,
      { form: ExpenseEvaluationParametersForm; evaluationId: string | number }
    >({
      query: ({ form, evaluationId }) => ({
        url: `/${evaluationId}/save-expense-evaluation-parameters`,
        method: 'POST',
        data: form
      }),
      invalidatesTags: (res, _err, { evaluationId }) =>
        res ? [{ type: 'EVALUATION', evaluationId }] : ['EVALUATION']
    }),

    saveExpenseEvaluationHusbandryParameters: build.mutation<
      ExpenseEvaluationResponse,
      {
        form: ExpenseEvaluationHusbandryParametersForm;
        evaluationId: string | number;
      }
    >({
      query: ({ form, evaluationId }) => ({
        url: `/${evaluationId}/save-expense-evaluation-husbandry-parameters`,
        method: 'POST',
        data: form
      }),
      invalidatesTags: (res, _err, { evaluationId }) =>
        res ? [{ type: 'EVALUATION', evaluationId }] : ['EVALUATION']
    }),

    restoreEvaluationExpense: build.mutation<
      ExpenseEvaluationResponse,
      { expense: ExpenseEvaluationResponse }
    >({
      query: ({ expense }) => ({
        url: `/${expense.evaluationId}/restore-expense-evaluation`,
        method: 'POST',
        data: expense.id
      }),
      invalidatesTags: (res, _err, { expense }) =>
        res
          ? [
              { type: 'EVALUATION', id: expense.evaluationId },
              'BudgetStatus',
              'BudgetCommit'
            ]
          : ['EVALUATION']
    }),

    splitExpenseEvaluation: build.mutation<
      ExpenseEvaluationResponse,
      {
        evaluationId: string | number;
        payload: {
          id: number;
          departmentId: number;
          expenseAccountItems: {
            accountingCodeId: number;
            amount: number;
          }[];
        };
      }
    >({
      query: ({ payload, evaluationId }) => {
        const data = PsUtilities.createFormRequestParts(payload, undefined);

        return {
          url: `/${evaluationId}/split-expense-evaluation`,
          method: 'POST',
          data,
          formData: true,
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        };
      },
      invalidatesTags: (res, _err, { evaluationId }) =>
        res ? [{ type: 'EVALUATION', id: evaluationId }] : ['EVALUATION']
    }),

    editExpenseEvaluation: build.mutation<
      ExpenseEvaluationResponse,
      { evaluationId: string | number; form: ExpenseEvaluationEditForm }
    >({
      query: ({ evaluationId, form }) => ({
        method: 'POST',
        url: `/${evaluationId}/edit-expense-evaluation`,
        data: form
      }),
      invalidatesTags: (res, _err, { evaluationId }) =>
        res ? [{ type: 'EVALUATION', id: evaluationId }] : ['EVALUATION']
    }),

    editHusbandryExpenseEvaluation: build.mutation<
      ExpenseEvaluationResponse,
      {
        evaluationId: string | number;
        form: ExpenseEvaluationEditHusbandryForm;
      }
    >({
      query: ({ evaluationId, form }) => ({
        method: 'POST',
        url: `/${evaluationId}/edit-husbandry-expense-evaluation`,
        data: form
      }),
      invalidatesTags: (res, _err, { evaluationId }) =>
        res ? [{ type: 'EVALUATION', id: evaluationId }] : ['EVALUATION']
    }),

    deleteChildExpenseEvaluation: build.mutation<
      ExpenseEvaluationResponse,
      { childExpense: ExpenseEvaluationResponse }
    >({
      query: ({ childExpense }) => ({
        method: 'POST',
        url: `/${childExpense.evaluationId}/delete-child-expense-evaluation`,
        data: childExpense.id
      }),
      invalidatesTags: (res, _err, { childExpense }) =>
        res
          ? [
              { type: 'EVALUATION', id: childExpense.evaluationId },
              'BudgetStatus',
              'BudgetCommit'
            ]
          : ['EVALUATION']
    }),

    backForReviewExpenseEvaluation: build.mutation<
      ExpenseEvaluationResponse,
      {
        evaluationId: string | number;
        id: string;
        comments: string;
      }
    >({
      query: ({ evaluationId, id, comments }) => ({
        url: `/${evaluationId}/back-for-review-expense-evaluation`,
        method: 'POST',
        data: { id, comments }
      }),
      invalidatesTags: (res, _err, { evaluationId }) =>
        res ? [{ type: 'EVALUATION', id: evaluationId }] : ['EVALUATION']
    }),

    divideSplitExpenseEvaluation: build.mutation<
      ExpenseEvaluationResponse,
      {
        evaluationId: string | number;
        formData: {
          id: string;
          parentId: string;
          selections: { accountingCodeId: string; amount: number }[];
        };
      }
    >({
      query: ({ evaluationId, formData }) => ({
        url: `/${evaluationId}/divide-split-expense-evaluation`,
        method: 'POST',
        data: formData
      }),
      invalidatesTags: (res, _err, { evaluationId }) =>
        res ? [{ type: 'EVALUATION', id: evaluationId }] : ['EVALUATION']
    }),

    reAllocateSplitExpenseEvaluation: build.mutation<
      ExpenseEvaluationResponse,
      {
        evaluationId: string | number;
        formData: {
          splitExpenseEvaluationId: string;
          department: {
            id: string;
            name: string;
            abbreviation: string;
          };
          accountingCodeId: string;
        };
      }
    >({
      query: ({ evaluationId, formData }) => ({
        url: `/${evaluationId}/reallocate-split-expense-evaluation`,
        method: 'POST',
        data: formData
      }),
      invalidatesTags: (res, _err, { evaluationId }) =>
        res
          ? [
              { type: 'EVALUATION', id: evaluationId },
              'BudgetStatus',
              'BudgetCommit'
            ]
          : ['EVALUATION']
    }),

    revertExpenseEvaluation: build.mutation<
      ExpenseEvaluationResponse,
      {
        evaluationId: string | number;
        expenseId: string | number;
      }
    >({
      query: ({ evaluationId, expenseId }) => ({
        url: `/${evaluationId}/revert-expense-evaluation`,
        method: 'POST',
        data: expenseId
      }),
      invalidatesTags: (res, _err, { evaluationId }) =>
        res
          ? [
              { type: 'EVALUATION', id: evaluationId },
              'BudgetStatus',
              'BudgetCommit'
            ]
          : ['EVALUATION']
    })
  }),
  overrideExisting: false
});

export const {
  useVerifyExpenseEvaluationMutation,
  useSaveExpenseEvaluationParametersMutation,
  useSaveExpenseEvaluationHusbandryParametersMutation,
  useRestoreEvaluationExpenseMutation,
  useSplitExpenseEvaluationMutation,
  useEditExpenseEvaluationMutation,
  useEditHusbandryExpenseEvaluationMutation,
  useDeleteChildExpenseEvaluationMutation,
  useBackForReviewExpenseEvaluationMutation,
  useDivideSplitExpenseEvaluationMutation,
  useReAllocateSplitExpenseEvaluationMutation,
  useRevertExpenseEvaluationMutation
} = expenseEvaluationApi;
